import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import notify from '@/lib/notify';
import { Interview } from '../../../types';
import { getUseCaseStatusLabel } from '../../UseCases/useCaseStatus';

import { useTranslation } from 'react-i18next';

// Interface des props, adaptée pour le Cadrage
interface CadrageInterviewsSectionProps {
  interviews: Interview[];
  isLoading: boolean;
  error: string | null;
  // selectedInterviewId: string | null; // Plus nécessaire ici si la sélection est gérée par la modale
  // selectInterview: (id: string | null) => void; // Plus nécessaire ici
  onTriggerNewInterview: () => void;
  selectInterviewToEdit: (interview: Interview) => void;
  openEditModal: () => void;
  onViewInterview: (interview: Interview) => void;
  onDeleteInterview: (interviewId: string) => Promise<void>;
  isReadOnly?: boolean;
}

// Helper pour formater la date (peut être externalisé)
const formatDateSimple = (
  dateString: string | null | undefined,
  t: (key: string, defaultValue?: string) => string,
  locale: string,
): string => {
    if (!dateString) return t('global.notAvailable', 'N/A');
    try {
        return new Date(dateString).toLocaleDateString(locale);
    } catch {
        return t('cadrage.interviews.invalidDate', 'Date invalide');
    }
};

const CadrageInterviewsSection: React.FC<CadrageInterviewsSectionProps> = ({
  interviews,
  isLoading,
  error,
  onTriggerNewInterview,
  selectInterviewToEdit,
  openEditModal,
  onViewInterview,
  onDeleteInterview,
  isReadOnly = false,
}) => {
  const { t, i18n } = useTranslation();

  const handleDeleteClick = async (interviewId: string, interviewName: string | null) => {
      // La confirmation peut être gérée ici ou dans le hook/page parente
      const displayName = interviewName || `${interviewId.substring(0,6)}...`;
      if (!window.confirm(t('cadrage.interviews.deleteConfirm', 'Supprimer l\'entretien "{{name}}" ? Cette action est irréversible.', { name: displayName }))) return;
      try {
          await onDeleteInterview(interviewId);
      } catch (error) {
          console.error(t('cadrage.interviews.deleteErrorLog', 'Erreur lors de la suppression de l\'entretien (composant):'), error);
          notify.error(`${t('cadrage.interviews.deleteError', 'Échec de la suppression:')} ${error instanceof Error ? error.message : String(error)}`);
      }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg shadow">
        {/* En-tête de la section */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">{t('cadrage.interviews.title', 'Entretiens Guidés (Cadrage)')}</h2>
          {!isReadOnly && (
            <button
              onClick={onTriggerNewInterview}
              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90"
              disabled={isLoading}
            >
              <Plus className="h-4 w-4 mr-1" /> {t('cadrage.interviews.addButton', 'Ajouter un entretien')}
            </button>
          )}
        </div>

        {/* Affichage Erreur / Chargement / Liste */}
        <div className="p-6">
          {isLoading && <p className="text-muted-foreground italic">{t('cadrage.interviews.loading', 'Chargement des entretiens...')}</p>}
          {error && <p className="text-red-500">{t('cadrage.interviews.error', 'Erreur:')} {error}</p>}
          {!isLoading && !error && interviews.length === 0 && (
            <p className="text-muted-foreground italic">{t('cadrage.interviews.empty', 'Aucun entretien guidé pour ce cadrage.')}</p>
          )}
          {!isLoading && !error && interviews.length > 0 && (
            <ul className="divide-y divide-border">
              {interviews.map((interview) => (
                <li key={interview.id} className="py-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-foreground">{interview.name || t('cadrage.interviews.interviewFallback', 'Entretien (ID: {{id}}...)', { id: interview.id.substring(0, 6) })}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {t('global.status', 'Statut:')} <span className={`font-medium ${interview.status === 'Completed' ? 'text-green-600' : (interview.status === 'In Progress' ? 'text-yellow-600' : 'text-muted-foreground')}`}>{interview.status ? getUseCaseStatusLabel(interview.status, t) : t('global.notAvailable', 'N/A')}</span>
                        {interview.interview_date && ` | ${t('global.date', 'Date:')} ${formatDateSimple(interview.interview_date, t, i18n.language)}`}
                        {interview.interviewee_name && ` | ${t('cadrage.interviews.interviewee', 'Interviewé:')} ${interview.interviewee_name}`}
                    </p>
                    {interview.updatedAt && interview.updatedAt !== interview.createdAt && (
                         <p className="text-xs text-muted-foreground">{t('cadrage.interviews.lastModified', 'Dernière modif:')} {formatDateSimple(interview.updatedAt, t, i18n.language)}</p>
                    )}
                  </div>
                  {/* Boutons d'action */}
                  <div className="flex space-x-2 flex-shrink-0">
                    <button
                      onClick={() => onViewInterview(interview)}
                      className="inline-flex items-center px-3 py-1 border border-border shadow-sm text-sm leading-4 font-medium rounded-md text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                      disabled={isLoading}
                      title={t('cadrage.interviews.viewTitle', 'Voir l\'entretien')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {t('cadrage.interviews.view', 'Voir')}
                    </button>
                    {!isReadOnly && (
                      <>
                        <button
                          onClick={() => {
                            selectInterviewToEdit(interview);
                            openEditModal();
                          }}
                          className="inline-flex items-center px-3 py-1 border border-border shadow-sm text-sm leading-4 font-medium rounded-md text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                          disabled={isLoading}
                          title={t('cadrage.interviews.editTitle', 'Modifier l\'entretien')}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          {t('cadrage.interviews.edit', 'Modifier')}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(interview.id, interview.name)}
                          className="inline-flex items-center p-1 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-background hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          disabled={isLoading}
                          title={t('cadrage.interviews.deleteTitle', 'Supprimer l\'entretien')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {/* La modale d'édition est gérée par la page parente (CadrageDetailPage) */}
    </div>
  );
};

export default CadrageInterviewsSection;
